import * as JSZip from "jszip";
import mammoth from "mammoth";
import { useParseWordDocumentStyles } from "./useParseWordDocumentStyles";
import {
  useStripWordDocumentImagesFromHtml,
  useWordDocumentHtmlConversionOptions,
} from "./useWordDocumentHtmlConversionOptions";
import {
  type ImportedRevisionHeaders,
  useExtractDocxPageHeaders,
} from "./useExtractDocxPageHeaders";
import type {
  WordDocumentHtmlConversionResult,
  WordDocumentHtmlImportOptions,
} from "./useConvertGoogleDocToHtml";
import { useExtractWritingCompanionChecklist } from "./useExtractWritingCompanionChecklist";

/**
 * Converts a Word document file to HTML
 * @param file - The Word document file to convert
 * @returns Promise that resolves to HTML string or throws an error
 */
export const useConvertWordFileToHtmlWithHeaders = async (
  file: File,
  options: WordDocumentHtmlImportOptions = {}
): Promise<WordDocumentHtmlConversionResult> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = async (event) => {
      try {
        const arrayBuffer = event.target?.result as ArrayBuffer;

        if (!arrayBuffer) {
          reject(new Error("Failed to read file as ArrayBuffer"));
          return;
        }

        // Step 1: Convert Word document to HTML using mammoth
        const result = await mammoth.convertToHtml(
          { arrayBuffer },
          useWordDocumentHtmlConversionOptions() as any
        );
        let html = result.value;
        let headers: ImportedRevisionHeaders | undefined;
        let documentXml: string | undefined;

        // Step 2: Parse the Word document XML to extract style information
        try {
          const zip = await JSZip.loadAsync(arrayBuffer);
          documentXml = await zip
            .file("word/document.xml")
            ?.async("string");
          const stylesXml = await zip.file("word/styles.xml")?.async("string");
          const numberingXml = await zip.file("word/numbering.xml")?.async("string");
          const relsXml = await zip
            .file("word/_rels/document.xml.rels")
            ?.async("string");

          if (documentXml) {
            html = useParseWordDocumentStyles(documentXml, html, stylesXml, numberingXml);
            headers = await useExtractDocxPageHeaders(
              zip,
              documentXml,
              relsXml
            );
          }
        } catch (error) {
          console.warn("Failed to parse Word document XML for styles:", error);
          // Continue with mammoth's HTML even if style extraction fails
        }

        html = useStripWordDocumentImagesFromHtml(html);
        const checklistExtraction =
          options.extractWritingCompanionChecklist && documentXml
            ? useExtractWritingCompanionChecklist({ documentXml, html })
            : { html };

        resolve({
          html: checklistExtraction.html,
          headers,
          importedChecklist: checklistExtraction.importedChecklist,
        });
      } catch (error) {
        reject(
          error instanceof Error
            ? error
            : new Error("Failed to convert Word file to HTML")
        );
      }
    };

    fileReader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    fileReader.readAsArrayBuffer(file);
  });
};

export const useConvertWordFileToHtml = async (
  file: File,
  options: WordDocumentHtmlImportOptions = {}
): Promise<string> => {
  return (await useConvertWordFileToHtmlWithHeaders(file, options)).html;
};
